// src/components/SEO.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  path, 
  image, 
  type = 'website',
  keywords = [],
  author = 'Kajiado Bright Horizons',
  publishedDate,
  modifiedDate,
  children 
}) => {
  const siteName = 'Kajiado Bright Horizons';
  
  // ✅ UPDATED: Include both old and new names for SEO
  const defaultDescription = 'Kajiado Bright Horizons (formerly Kajiado Children\'s Home - KCH) has been providing hope, care, and education to vulnerable children and orphans in Kajiado, Kenya since 1997. Support orphaned and vulnerable children through sponsorship, donations, and volunteering.';
  
  const defaultImage = 'https://kajiadobrighthorizons.org/images/og-image.jpg';
  
  // ✅ FIXED: Use main domain, NOT Vercel
  const siteUrl = 'https://kajiadobrighthorizons.org';
  const url = `${siteUrl}${path || ''}`;
  
  const metaDescription = description || defaultDescription;
  const metaImage = image || defaultImage;
  
  // ✅ UPDATED: Include old name (KCH) in keywords
  const metaKeywords = keywords.length > 0 ? keywords.join(', ') : 
    'Kajiado Bright Horizons, Kajiado Childrens Home, KCH, childrens home, orphanage Kajiado, charity, donations, child sponsorship, Kenya, Kajiado, vulnerable children, Christian care, education, shelter, feeding program, help orphans Kenya, Kajiado orphanage, childrens charity Kenya, sponsor a child Kajiado, Kajiado rescue center, Kajiado Childrens Home KCH, former Kajiado Childrens Home';

  // Organization structured data - includes both names
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Kajiado Bright Horizons',
    alternateName: 'Kajiado Children\'s Home',
    url: siteUrl,
    logo: 'https://kajiadobrighthorizons.org/logo.png',
    description: 'Kajiado Bright Horizons (formerly Kajiado Children\'s Home - KCH) providing hope, care, and education to vulnerable children in Kajiado, Kenya since 1997.',
    email: 'info@kajiadochildrenhome.org',
    telephone: '+254 794 681 030',
    foundingDate: '1997',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Kajiado',
      addressRegion: 'Kajiado County',
      addressCountry: 'KE',
      streetAddress: 'Kajiado Town'
    },
    sameAs: [
      'https://www.facebook.com/kajiadochildrenshome',
      'https://twitter.com/kajiadochildren',
      'https://www.instagram.com/kajiadochildrenshome'
    ]
  };

  // Website structured data
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    alternateName: 'Kajiado Children\'s Home',
    url: siteUrl,
    description: defaultDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  // Breadcrumb structured data (if path is provided)
  const breadcrumbSchema = path ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title || 'Kajiado Bright Horizons',
        item: url
      }
    ]
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title ? `${title} | ${siteName}` : siteName}</title>
      <meta name="title" content={title ? `${title} | ${siteName}` : siteName} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content="index, follow" />
      <meta name="author" content={author} />
      
      {/* ✅ CANONICAL URL - Points to main domain */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title ? `${title} | ${siteName}` : siteName} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_KE" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title ? `${title} | ${siteName}` : siteName} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:site" content="@kajiadochildren" />
      <meta name="twitter:creator" content="@kajiadochildren" />
      
      {/* Article meta (if type is article) */}
      {type === 'article' && (
        <>
          <meta property="article:published_time" content={publishedDate} />
          <meta property="article:modified_time" content={modifiedDate} />
          <meta property="article:author" content={author} />
          <meta property="article:tag" content={metaKeywords} />
        </>
      )}
      
      {/* Favicon and App Icons */}
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      <meta name="theme-color" content="#2563eb" />
      
      {/* Additional SEO */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="msapplication-tap-highlight" content="no" />
      
      {/* ✅ Structured Data - Now points to .org with both names */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

export default SEO;